import React from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import ContactInfoReveal from './ContactInfoReveal';
import ListingMap from './ListingMap';
import YouTube from 'react-youtube';

interface ContactInfo {
  address?: string;
  latitude?: number;
  longitude?: number;
  supportPhone?: string;
}

interface DescriptionSectionProps {
  listing: {
    _id: string;
    title: string;
    description: string;
    address?: string;
    showAddress: string;
    showOnMap: boolean;
    latitude?: number;
    longitude?: number;
    approximateLatitude?: number;
    approximateLongitude?: number;
    supportPhoneNumber?: string;
    showSupportPhone: string;
    youtubeVideos: string[];
  };
  revealedContactInfo: ContactInfo | null;
  handleContactReveal: (info: ContactInfo) => void;
  getYouTubeVideoId: (url: string) => string | null;
}

const DescriptionSection: React.FC<DescriptionSectionProps> = ({
  listing,
  revealedContactInfo,
  handleContactReveal,
  getYouTubeVideoId,
}) => {
  return (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Description</h2>
      <MarkdownRenderer content={listing.description} className="text-gray-600 mb-6" />
      
      {/* Two-column layout for contact info and map */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Left column: Address and Phone */}
        <div>
          {/* Address display based on visibility setting */}
          {listing.showAddress === 'hide' ? null : listing.showAddress === 'renters_only' ? (
            <div className="mb-4">
              <div className="font-medium text-gray-900">Address:</div>
              <div className="text-gray-600 italic">Address available after booking</div>
            </div>
          ) : listing.showAddress === 'public' ? (
            listing.address ? (
              <div className="mb-4">
                <div className="font-medium text-gray-900">Address:</div>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(listing.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  {listing.address}
                </a>
              </div>
            ) : (
              <div className="mb-4">
                <div className="font-medium text-gray-900">Address:</div>
                <div className="text-gray-600 italic">No address set</div>
              </div>
            )
          ) : (listing.showAddress === 'captcha' || listing.showAddress === 'always') ? (
            revealedContactInfo?.address ? (
              <div className="mb-4">
                <div className="font-medium text-gray-900">Address:</div>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(revealedContactInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  {revealedContactInfo.address}
                </a>
              </div>
            ) : (
              <div className="mb-4">
                <div className="font-medium text-gray-900">Address:</div>
              </div>
            )
          ) : null}
          
          {/* Support Phone - only shown if revealed via CAPTCHA or not protected */}
          {listing.showSupportPhone === 'hide' ? null : listing.showSupportPhone === 'renters_only' ? (
            <div className="mb-4">
              <div className="font-medium text-gray-900">Phone:</div>
              <div className="text-gray-600 italic">Phone Number available after booking</div>
            </div>
          ) : listing.showSupportPhone === 'everyone' && revealedContactInfo?.supportPhone ? (
            <div className="mb-4">
              <div className="font-medium text-gray-900">Phone:</div>
              <div className="text-gray-700">{revealedContactInfo.supportPhone}</div>
            </div>
          ) : listing.showSupportPhone === 'everyone' && !revealedContactInfo ? (
            <div className="mb-4">
              <div className="font-medium text-gray-900">Phone:</div>
            </div>
          ) : null}

          {/* Contact Info Reveal (for CAPTCHA-protected info) */}
          {((listing.showAddress === 'captcha' || listing.showAddress === 'always') || listing.showSupportPhone === 'everyone') && !revealedContactInfo && (
            <div className="mb-4">
              <ContactInfoReveal
                listingId={listing._id}
                showAddress={listing.showAddress}
                showSupportPhone={listing.showSupportPhone}
                onReveal={handleContactReveal}
              />
            </div>
          )}
        </div>

        {/* Right column: Map */}
        {listing.showOnMap && (listing.latitude || listing.approximateLatitude) && (
          <div>
            <div className="font-medium text-gray-900 mb-2">Map:</div>
            <ListingMap
              latitude={
                listing.showAddress === 'public' ? (listing.latitude || 0) :
                revealedContactInfo?.latitude || listing.approximateLatitude || listing.latitude || 0
              }
              longitude={
                listing.showAddress === 'public' ? (listing.longitude || 0) :
                revealedContactInfo?.longitude || listing.approximateLongitude || listing.longitude || 0
              }
              showExact={listing.showAddress === 'public' || !!revealedContactInfo}
              title={listing.title}
            />
          </div>
        )}
      </div>

      {/* YouTube Videos */}
      {listing.youtubeVideos && listing.youtubeVideos.length > 0 && (
        <div className="mt-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Videos</h2>
          <div className="space-y-4">
            {listing.youtubeVideos.map((videoUrl, index) => {
              const videoId = getYouTubeVideoId(videoUrl);
              return videoId ? (
                <div key={index} className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <YouTube
                    videoId={videoId}
                    opts={{
                      width: '100%',
                      height: '100%',
                      playerVars: {
                        autoplay: 0,
                        rel: 0,
                        modestbranding: 1,
                      },
                    }}
                    className="w-full h-full"
                    onReady={() => {
                      // Player is ready, no action needed
                    }}
                    onError={(error: { target: number; data: number }) => {
                      console.error('YouTube player error:', error);
                    }}
                  />
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DescriptionSection;

